import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { 
  Search, 
  Filter, 
  Building, 
  ArrowLeft,
  Users,
  Calendar,
  Target,
  BarChart,
  Star,
  Brain,
  Clock,
  MapPin,
  Briefcase,
  CheckCircle,
  XCircle
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { format } from 'date-fns';

export default function AdminProjectsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');

  useEffect(() => {
    if (user) {
      fetchProjects();
    }
  }, [user]);

  const fetchProjects = async () => {
    try {
      const { data, error } = await supabase
        .from('recruiting_projects')
        .select(`
          *,
          profiles (
            id,
            full_name,
            company_name
          ),
          applications (
            id,
            status,
            candidate_stage_progress (
              id,
              stage_id,
              status,
              completed_at
            )
          ),
          interview_stage_templates (
            id,
            name,
            order_index
          ),
          job_matches (
            id,
            profile_id,
            overall_score,
            skills_score,
            experience_score,
            match_details
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Calculate progress and metrics for each project
      const projectsWithMetrics = (data || []).map(project => {
        const totalStages = project.interview_stage_templates?.length || 1;
        const totalApplications = project.applications?.length || 0;
        const matchedCandidates = project.job_matches?.length || 0;
        const highMatchCandidates = project.job_matches?.filter((m: any) => m.overall_score >= 80).length || 0;
        
        // Calculate stage progress
        let completedStages = 0;
        if (totalApplications > 0) {
          completedStages = project.applications.reduce((sum: number, app: any) => {
            const stagesCompleted = app.candidate_stage_progress?.filter(
              (progress: any) => progress.status === 'completed'
            ).length || 0;
            return sum + stagesCompleted;
          }, 0);
        }

        // Calculate average progress
        const avgStagesCompleted = totalApplications > 0 ? completedStages / totalApplications : 0;
        const progress = Math.round((avgStagesCompleted / totalStages) * 100);

        // Calculate match quality
        const matchQuality = matchedCandidates > 0 ? 
          Math.round((highMatchCandidates / matchedCandidates) * 100) : 0;

        return {
          ...project,
          metrics: {
            progress,
            totalApplications,
            matchedCandidates,
            highMatchCandidates,
            matchQuality,
            avgStagesCompleted
          }
        };
      });

      setProjects(projectsWithMetrics);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load projects'
      });
    } finally {
      setLoading(false);
    }
  };

  // Get unique departments for filtering
  const departments = [...new Set(projects.map(project => project.department))].filter(Boolean);

  // Filter and sort projects
  const filteredProjects = projects.filter(project => {
    if (statusFilter !== 'all' && project.is_active !== (statusFilter === 'active')) {
      return false;
    }
    
    if (departmentFilter !== 'all' && project.department !== departmentFilter) {
      return false;
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        project.title?.toLowerCase().includes(query) ||
        project.department?.toLowerCase().includes(query) ||
        project.location?.toLowerCase().includes(query) ||
        project.profiles?.company_name?.toLowerCase().includes(query)
      );
    }
    
    return true;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'date':
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      case 'applications':
        return (b.metrics.totalApplications || 0) - (a.metrics.totalApplications || 0);
      case 'matches':
        return (b.metrics.matchQuality || 0) - (a.metrics.matchQuality || 0);
      case 'progress':
        return (b.metrics.progress || 0) - (a.metrics.progress || 0);
      default:
        return 0;
    }
  });

  return (
    <div className="container py-8 px-4 md:px-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Recruiting Projects</h1>
            <p className="text-muted-foreground">
              Manage and monitor recruiting projects across the platform
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input 
                placeholder="Search projects..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          
          <div className="flex gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>

            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map(dept => (
                  <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date">Date Created</SelectItem>
                <SelectItem value="applications">Applications</SelectItem>
                <SelectItem value="matches">Match Quality</SelectItem>
                <SelectItem value="progress">Progress</SelectItem>
              </SelectContent>
            </Select>

            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => {
                setSearchQuery('');
                setStatusFilter('all');
                setDepartmentFilter('all');
                setSortBy('date');
              }}
            >
              <Filter className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Projects List */}
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          </div>
        ) : filteredProjects.length === 0 ? (
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <Building className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No Projects Found</h3>
                <p className="text-muted-foreground">
                  {searchQuery || statusFilter !== 'all' || departmentFilter !== 'all'
                    ? 'Try adjusting your filters'
                    : 'No recruiting projects available'}
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {filteredProjects.map((project) => (
              <Card 
                key={project.id} 
                className={`hover:bg-muted/50 transition-colors cursor-pointer ${
                  project.is_active ? 'border-primary/20' : ''
                }`}
                onClick={() => navigate(`/admin/projects/${project.id}`)}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-medium">{project.title}</h3>
                          <p className="text-sm text-muted-foreground">
                            {project.profiles?.company_name} • {project.department}
                          </p>
                        </div>
                        <Badge variant={project.is_active ? 'default' : 'secondary'}>
                          {project.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>

                      <div className="flex flex-wrap gap-4 text-sm">
                        <div className="flex items-center gap-1">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span>{project.location}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span>Created {format(new Date(project.created_at), 'MMM d, yyyy')}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          <span>{project.metrics.totalApplications} applicants</span>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 mt-4">
                        {project.skills?.slice(0, 5).map((skill: string, index: number) => (
                          <Badge key={index} variant="secondary">
                            {skill}
                          </Badge>
                        ))}
                        {project.skills?.length > 5 && (
                          <Badge variant="outline">
                            +{project.skills.length - 5} more
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-col gap-4 min-w-[200px]">
                      <div>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm text-muted-foreground">Progress</span>
                          <span className="text-sm font-medium">{project.metrics.progress}%</span>
                        </div>
                        <Progress value={project.metrics.progress} className="h-2" />
                      </div>

                      <div>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm text-muted-foreground">Match Quality</span>
                          <span className="text-sm font-medium">{project.metrics.matchQuality}%</span>
                        </div>
                        <Progress value={project.metrics.matchQuality} className="h-2" />
                      </div>

                      <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Brain className="h-4 w-4 text-primary" />
                            <span className="text-sm">AI Matches</span>
                          </div>
                          <Badge variant="secondary">
                            {project.metrics.highMatchCandidates} high match
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Target className="h-4 w-4 text-primary" />
                            <span className="text-sm">Stage Progress</span>
                          </div>
                          <Badge variant="outline">
                            Stage {Math.floor(project.metrics.avgStagesCompleted)} of {project.interview_stage_templates?.length}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}